import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AdminService } from 'src/app/services/admin.service';
import { Popover } from 'primeng/popover';

export interface User {
  _id: string;
  fullName: string;
  email: string;
  profileImg?: string;
  role?: string;
}

@Component({
  selector: 'app-user-selector',
  templateUrl: './user-selector.component.html',
  styleUrls: ['./user-selector.component.scss'],
  standalone: false,
})
export class UserSelectorComponent implements OnInit, OnChanges {
  @Input() projectId: string = '';
  @Input() users: User[] = []; // Accept users from parent component
  @Input() selectedUserIds: string[] = [];
  @Input() placeholder: string = 'Select users...';
  @Input() multiSelect: boolean = true;
  @Input() quickSelect: boolean = false; // If true, clicking user info selects one and closes; clicking + icon adds to selection
  @Input() currentUserId?: string; // To highlight current user
  @Output() selectedUsersChange = new EventEmitter<string[]>();

  @ViewChild('popover') popover!: Popover;

  internalUsers: User[] = []; // Internal users list
  filteredUsers: User[] = [];
  searchQuery: string = '';
  isLoading: boolean = false;

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    // If users are provided, use them; otherwise load from API
    if (this.users && this.users.length > 0) {
      this.internalUsers = this.users;
      this.filteredUsers = [...this.internalUsers];
      console.log('[UserSelector] Using provided users:', this.internalUsers.length);
    } else {
      this.loadProjectMembers();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If users input changes, update internal list
    if (changes['users'] && !changes['users'].firstChange) {
      console.log('[UserSelector] Users input changed:', changes['users'].currentValue);
      this.internalUsers = changes['users'].currentValue || [];
      this.filteredUsers = [...this.internalUsers];
    }

    // If projectId changes and no users provided, reload from API
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      if (!this.users || this.users.length === 0) {
        this.loadProjectMembers();
      }
    }
  }

  loadProjectMembers(): void {
    if (!this.projectId) {
      console.warn('No project ID provided to load members');
      return;
    }

    console.log('[UserSelector] Loading members for project:', this.projectId);
    this.isLoading = true;
    this.adminService.projectteamMembers(this.projectId).subscribe({
      next: (res: any) => {
        console.log('[UserSelector] API Response:', res);
        const raw = res?.data || (Array.isArray(res) ? res : null);
        if (raw) {
          this.internalUsers = raw.map((member: any) => {
            const u = member.userId ?? member;
            return {
              ...u,
              fullName: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userName || 'User',
            };
          });
          this.filteredUsers = [...this.internalUsers];
          console.log('[UserSelector] Loaded users:', this.internalUsers.length);
        } else {
          console.warn('[UserSelector] Invalid response format:', res);
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('[UserSelector] Error loading project members:', error);
        this.isLoading = false;
      },
    });
  }

  onSearch(): void {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredUsers = [...this.internalUsers];
      return;
    }

    this.filteredUsers = this.internalUsers.filter(
      (user) =>
        (user.fullName || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query)
    );
  }

  /**
   * Handle clicking on user info area (quick select mode)
   */
  onUserInfoClick(userId: string, event: Event): void {
    event.stopPropagation();

    if (this.quickSelect && this.multiSelect) {
      // In quick select mode: clicking user info selects only this user and closes
      this.selectedUserIds = [userId];
      this.selectedUsersChange.emit(this.selectedUserIds);
      this.popover.hide();
    } else {
      // Normal mode: toggle selection
      this.toggleUser(userId);
    }
  }

  /**
   * Handle clicking on the action icon (+ or check)
   */
  onIconClick(userId: string, event: Event): void {
    event.stopPropagation();

    if (this.quickSelect && this.multiSelect) {
      // In quick select mode: clicking icon adds to selection (multi-select)
      this.toggleUser(userId);
    } else {
      // Normal mode: same as user info click
      this.toggleUser(userId);
    }
  }

  /**
   * Toggle user selection (add/remove from selection)
   */
  toggleUser(userId: string): void {
    if (this.multiSelect) {
      const index = this.selectedUserIds.indexOf(userId);
      if (index > -1) {
        this.selectedUserIds = this.selectedUserIds.filter((id) => id !== userId);
      } else {
        this.selectedUserIds = [...this.selectedUserIds, userId];
      }
    } else {
      this.selectedUserIds = [userId];
      // In single-select mode, close popover after selection
      if (this.popover) {
        this.popover.hide();
      }
    }

    this.selectedUsersChange.emit(this.selectedUserIds);
  }

  isSelected(userId: string): boolean {
    return this.selectedUserIds.includes(userId);
  }

  isCurrentUser(userId: string): boolean {
    return this.currentUserId === userId;
  }

  getSelectedUsers(): User[] {
    return this.internalUsers.filter((user) => this.selectedUserIds.includes(user._id));
  }

  get selectedCount(): number {
    return this.selectedUserIds.length;
  }

  getInitials(name: string): string {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getProfileImage(user: User): string {
    if (user.profileImg && !user.profileImg.includes('undefined')) {
      return user.profileImg;
    }
    const name = user.fullName || 'U';
    const initials = name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase();
    const colors = ['4f46e5','7c3aed','0891b2','059669','d97706','dc2626'];
    const color = colors[name.charCodeAt(0) % colors.length];
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${color}&color=fff&size=64&bold=true`;
  }

  removeUser(userId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedUserIds = this.selectedUserIds.filter((id) => id !== userId);
    this.selectedUsersChange.emit(this.selectedUserIds);
  }

  clearAll(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedUserIds = [];
    this.selectedUsersChange.emit(this.selectedUserIds);
  }
}
